export interface FloorLatch {
  agentRunId: string | null | undefined;
  userMessageId: string | null;
}

export interface FloorLatchSnapshot {
  agentRunId: string | null | undefined;
  latestUserMessageId: string | null;
  /** `deriveLatestTurnNeedsRoomLive(...)` for the current render. */
  liveNeedsRoom: boolean;
  /** `runFailed || runClosed || latestTurnIsTerminal`. */
  cleared: boolean;
}

/**
 * Keep the send-snap floor after a live turn settles. Dropping it for a short
 * reply collapses scrollHeight and the browser clamps scrollTop toward 0.
 * Latch is scoped to the latest user-message id so a follow-up send in the
 * same run cannot keep a stale floor, and cold historical loads never latch.
 *
 * Pure reducer: `(prev, snapshot) -> next`. Returns `prev` by reference when
 * nothing changes, so a caller can diff by identity to decide whether to
 * commit a state update.
 */
export const nextFloorLatch = (
  prev: FloorLatch,
  snapshot: FloorLatchSnapshot
): FloorLatch => {
  let next = prev;

  if (next.agentRunId !== snapshot.agentRunId) {
    next = { agentRunId: snapshot.agentRunId, userMessageId: null };
  }

  if (snapshot.cleared) {
    if (next.userMessageId != null) {
      next = { ...next, userMessageId: null };
    }
  } else if (snapshot.liveNeedsRoom && snapshot.latestUserMessageId != null) {
    if (next.userMessageId !== snapshot.latestUserMessageId) {
      next = { ...next, userMessageId: snapshot.latestUserMessageId };
    }
  } else if (
    next.userMessageId != null &&
    next.userMessageId !== snapshot.latestUserMessageId
  ) {
    next = { ...next, userMessageId: null };
  }

  return next;
};

export const computeLatestTurnFloorPx = (
  viewportHeight: number | null,
  insetPx: number
): number | null =>
  viewportHeight != null && viewportHeight > insetPx ? viewportHeight - insetPx : null;
